import React from 'react';
import { X, Copy, Download } from 'lucide-react';

const SIGN_HI = ['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या',
  'तुला', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन'];

const copy = (txt) => navigator.clipboard?.writeText(String(txt));

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function yearsBetween(startIso, endIso) {
  try {
    const a = new Date(startIso);
    const b = new Date(endIso);
    const totalDays = (b - a) / (1000 * 60 * 60 * 24);
    const y = Math.floor(totalDays / 365);
    const m = Math.floor((totalDays - y * 365) / 30);
    const d = Math.floor(totalDays - y * 365 - m * 30);
    return `${y}Y ${m}M ${d}D`;
  } catch { return '—'; }
}

const Shell = ({ title, subtitle, onClose, children, testid, actions }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
    <div className="relative w-full max-w-xl rounded-2xl border border-gold/40 bg-gradient-to-br from-purple-950 via-bg-primary to-purple-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <button data-testid="popup-close-btn" onClick={onClose} className="absolute top-4 right-4 text-text-muted hover:text-gold">
        <X className="w-5 h-5" />
      </button>
      <h3 className="font-heading text-2xl text-gold mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-text-muted mb-4">{subtitle}</p>}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">{children}</div>
      {actions && <div className="flex gap-2 mt-4 pt-3 border-t border-gold/20">{actions}</div>}
    </div>
  </div>
);

const Row = ({ label, value, mono, highlight, copyable }) => (
  <div className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 ${highlight ? 'bg-gold/10 border border-gold/30' : 'bg-white/5 border border-white/10'}`}>
    <div className="text-[11px] uppercase tracking-wider text-text-muted w-1/2">{label}</div>
    <div className={`text-sm flex-1 text-right ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>{value ?? '—'}</div>
    {copyable && value && (
      <button onClick={() => copy(value)} className="text-text-muted hover:text-gold flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
    )}
  </div>
);

// ── Dasha Popup ─────────────────────────────────────────────────────
export function DashaPopup({ dasha, level, data, onClose }) {
  if (!dasha) return null;

  const LEVEL_LABEL = {
    mahadasha: 'महादशा (Mahadasha)',
    antardasha: 'अंतर्दशा (Antardasha)',
    pratyantardasha: 'प्रत्यंतर्दशा (Pratyantardasha)',
    sookshma: 'सूक्ष्म दशा (Sookshma)',
    prana: 'प्राण दशा (Prana)',
  };

  // Look up D1 + D9 placement + status of the dasha lord planet
  const planetEn = dasha.lord_en;
  const popup = data?.popup_data?.[planetEn] || {};
  const d1Sign = data?.varga_charts?.D1?.planets?.[planetEn];
  const d9Sign = data?.varga_charts?.D9?.planets?.[planetEn];

  // Approx current age = (today - birth_dt) in years
  // birth_dt isn't passed directly; we can compute from data.birth_data
  let currentAge = '—';
  try {
    const bd = data?.birth_data;
    if (bd?.dob) {
      const birth = new Date(bd.dob + 'T' + (bd.tob || '00:00'));
      const ageMs = Date.now() - birth.getTime();
      currentAge = `${(ageMs / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1)} वर्ष`;
    }
  } catch (e) { /* ignore */ }

  const exportDasha = () => {
    const blob = new Blob([JSON.stringify(dasha, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dasha_${level}_${planetEn}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Shell
      title={`${dasha.lord_hi} · ${LEVEL_LABEL[level] || ''}`}
      subtitle={`${planetEn} · ${fmtDate(dasha.start)} → ${fmtDate(dasha.end)}`}
      onClose={onClose}
      testid="dasha-popup"
      actions={
        <>
          <button onClick={() => copy(JSON.stringify(dasha))} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-text-muted hover:text-gold text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
          <button onClick={exportDasha} data-testid="dasha-export-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold/15 border border-gold/30 text-gold text-xs hover:bg-gold/25"><Download className="w-3.5 h-3.5" /> Export JSON</button>
        </>
      }
    >
      {/* Time block */}
      <Row label="ग्रह स्वामी" value={dasha.lord_hi} highlight />
      <Row label="प्रारंभ तिथि" value={fmtDate(dasha.start)} mono />
      <Row label="समाप्ति तिथि" value={fmtDate(dasha.end)} mono />
      <Row label="कुल अवधि" value={yearsBetween(dasha.start, dasha.end)} mono />
      {dasha.remaining_str && <Row label="शेष अवधि" value={dasha.remaining_str} mono highlight />}
      <Row label="जातक की वर्तमान आयु" value={currentAge} />

      {/* D1 reference */}
      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">D1 (राशि) — स्वामी की स्थिति</div>
      <Row label="राशि" value={d1Sign !== undefined ? SIGN_HI[d1Sign] : '—'} />
      <Row label="भाव" value={popup.house ?? '—'} />
      <Row label="नक्षत्र" value={popup.nakshatra} />
      <Row label="पाद" value={popup.pada} />

      {/* D9 reference */}
      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">D9 (नवमांश) — स्वामी की स्थिति</div>
      <Row label="राशि" value={d9Sign !== undefined ? SIGN_HI[d9Sign] : '—'} />

      {/* Status */}
      {popup.status && (
        <>
          <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">स्थिति</div>
          <div className="flex flex-wrap gap-1.5">
            {popup.status.retrograde && <Tag t="↺ वक्री" />}
            {popup.status.combust && <Tag t="🔥 अस्त" />}
            {popup.status.exalted && <Tag t="↑ उच्च" />}
            {popup.status.debilitated && <Tag t="↓ नीच" />}
            {popup.status.own_sign && <Tag t="⚜ स्वराशि" />}
            {popup.status.mooltrikona && <Tag t="✦ मूलत्रिकोण" />}
            {popup.status.vargottama && <Tag t="□ वर्गोत्तम" />}
            {!popup.status.retrograde && !popup.status.combust && !popup.status.exalted && !popup.status.debilitated && !popup.status.own_sign && !popup.status.mooltrikona && !popup.status.vargottama && <span className="text-xs text-text-muted">सामान्य</span>}
          </div>
        </>
      )}
    </Shell>
  );
}

// ── Badhaka Popup ───────────────────────────────────────────────────
export function BadhakaPopup({ data, onClose }) {
  if (!data?.badhaka) return null;
  const b = data.badhaka;
  const place = b.badhakesh_placement || {};
  const d1Sign = data?.varga_charts?.D1?.planets?.[b.badhakesh_en];
  const d9Sign = data?.varga_charts?.D9?.planets?.[b.badhakesh_en];

  return (
    <Shell title={`बाधक विश्लेषण · ${b.badhakesh_hi}`} subtitle={`लग्न प्रकार: ${b.sign_type === 'movable' ? 'चर' : b.sign_type === 'fixed' ? 'स्थिर' : 'द्विस्वभाव'} · बाधक भाव: ${b.house}`} onClose={onClose} testid="badhaka-popup">
      <Row label="बाधक भाव संख्या" value={b.house} highlight />
      <Row label="बाधक राशि" value={b.badhaka_sign_hi} />
      <Row label="बाधकेश (स्वामी)" value={b.badhakesh_hi} highlight />

      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">बाधकेश की वर्तमान स्थिति</div>
      <Row label="राशि" value={place.sign_hi || '—'} />
      <Row label="भाव" value={place.house ?? '—'} />
      <Row label="अंश (DMS)" value={place.dms} mono copyable />
      <Row label="नक्षत्र" value={place.nakshatra} />
      <Row label="पाद" value={place.pada} />

      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">D1 · D9 राशि</div>
      <Row label="D1 राशि" value={d1Sign !== undefined ? SIGN_HI[d1Sign] : '—'} />
      <Row label="D9 राशि" value={d9Sign !== undefined ? SIGN_HI[d9Sign] : '—'} />

      <div className="pt-3 text-[10px] text-text-muted italic">केवल शास्त्रीय गणना। फलादेश नहीं।</div>
    </Shell>
  );
}

const Tag = ({ t }) => <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-900/40 border border-gold/30 text-gold">{t}</span>;
